import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const FEATURE_ROWS = [
  { feature: 'Dashboard & Analytics',           trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Dealer Management',               trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Stock Inventory',                 trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Finance & EMI Loans',             trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Collections Management',          trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Billing & Invoicing',             trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Purchase, Sales & Stock Reports', trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Exchange & Trade-In',             trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Seized Vehicle Tracking',         trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'User Management',                 trial: 'Unlimited', basic: true,          starter: false,       pro: 'Up to 3 users',   biz: 'Unlimited' },
  { feature: 'Outstanding & Collection Reports',trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Accounts (Business Summary)',     trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Outstanding Aging',               trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Customer Ledger',                 trial: true,        basic: false,         starter: false,       pro: false,             biz: true },
  { feature: 'Profit & Loss Analysis',          trial: true,        basic: false,         starter: false,       pro: false,             biz: true },
  { feature: 'Support Level',                   trial: 'Email',     basic: 'Email',       starter: 'Email',     pro: 'Priority Email',  biz: 'Phone & Email' },
];

function _cell(val) {
  if (val === true)  return <i className="fas fa-check-circle text-success"></i>;
  if (val === false) return <span className="text-muted">—</span>;
  return <small className="badge badge-light text-dark">{val}</small>;
}

function FeatureComparisonTable() {
  return (
    <div className="row mt-4">
      <div className="col-12">
        <div className="card card-outline card-info">
          <div className="card-header">
            <h3 className="card-title"><i className="fas fa-table mr-2"></i>Feature Comparison</h3>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="thead-dark">
                  <tr>
                    <th style={{minWidth:200}}>Feature</th>
                    <th className="text-center">Free Trial</th>
                    <th className="text-center">Basic</th>
                    <th className="text-center">Starter</th>
                    <th className="text-center">Professional</th>
                    <th className="text-center">Business</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_ROWS.map((row, i) => (
                    <tr key={i}>
                      <td className="font-weight-bold small">{row.feature}</td>
                      <td className="text-center">{_cell(row.trial)}</td>
                      <td className="text-center">{_cell(row.basic)}</td>
                      <td className="text-center">{_cell(row.starter)}</td>
                      <td className="text-center">{_cell(row.pro)}</td>
                      <td className="text-center">{_cell(row.biz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function computePriceDisplay(plan) {
    const price = parseFloat(plan.price) || 0;
    if (price === 0) {
        return { monthlyLabel: 'Free', annualNote: `${plan.duration_in_days} days — all features unlocked` };
    }
    if ((plan.duration_in_days || 0) >= 365) {
        const monthly = Math.round(price / 12);
        const fmtAnnual = price.toLocaleString('en-IN');
        return { monthlyLabel: `₹${monthly}/mo`, annualNote: `Billed annually at ₹${fmtAnnual}` };
    }
    return { monthlyLabel: null, annualNote: null };
}

export default function PricingPage() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get("/plan");
            const allPlans = res.data.data || [];
            const sortedPlans = [...allPlans].sort((a, b) => (a.price || 0) - (b.price || 0));
            setPlans(sortedPlans);
        } catch (err) {
            console.error("Error fetching plans:", err);
            Swal.fire({
                title: "Error",
                text: "Failed to load pricing information. Please try again.",
                icon: "error",
                confirmButtonColor: "#007bff"
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate discount percentage based on duration
    const calculateDiscount = (durationInDays) => {
        if (durationInDays >= 365) return 20;
        if (durationInDays >= 180) return 15;
        if (durationInDays >= 90) return 10;
        return 0;
    };

    // Format billing cycle display
    const formatBillingCycle = (durationInDays) => {
        if (durationInDays >= 365) return "year";
        if (durationInDays >= 180) return "6 months";
        if (durationInDays >= 90) return "3 months";
        return "month";
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(price);
    };

    if (loading) {
        return (
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Pricing Plans</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-md-6 text-center">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="spinner-border text-primary mb-3" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                        <p className="text-muted">Loading pricing information...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (plans.length === 0) {
        return (
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Pricing Plans</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-md-6">
                                <div className="card card-warning">
                                    <div className="card-header">
                                        <h3 className="card-title">No Plans Available</h3>
                                    </div>
                                    <div className="card-body text-center">
                                        <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                        <p>Sorry, there are no pricing plans available at the moment.</p>
                                        <button onClick={fetchPlans} className="btn btn-primary">
                                            <i className="fas fa-sync-alt mr-2"></i>
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="content-wrapper">
            {/* Content Header */}
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-tags text-primary mr-2"></i>
                                Our Pricing Plans
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item">Home</li>
                                <li className="breadcrumb-item active">Pricing</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <section className="content">
                <div className="container-fluid">
                    {/* Info callout */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="callout callout-info">
                                <h5>
                                    <i className="fas fa-tag mr-2"></i>
                                    All Plans Billed Annually
                                </h5>
                                Start with a free 14-day trial — no credit card required. Upgrade anytime.
                            </div>
                        </div>
                    </div>

                    {/* Plans Grid */}
                    <div className="row">
                        {plans.map((plan) => {
                            const discount = calculateDiscount(plan.duration_in_days);
                            const formattedCycle = formatBillingCycle(plan.duration_in_days);
                            
                            const { monthlyLabel, annualNote } = computePriceDisplay(plan);
                            const isPopular = !!plan.popular;

                            return (
                                <div key={plan.id} className="col-lg-3 col-md-6 mb-4">
                                    <div className={`card elevation-4 ${isPopular ? 'card-primary card-outline' : ''}`}
                                         style={{
                                             borderTop: isPopular ? '4px solid #007bff' : '2px solid #dee2e6',
                                             transition: 'transform 0.3s, box-shadow 0.3s'
                                         }}
                                         onMouseEnter={(e) => {
                                             e.currentTarget.style.transform = 'translateY(-10px)';
                                             e.currentTarget.style.boxShadow = '0 1rem 3rem rgba(0,0,0,.175)';
                                         }}
                                         onMouseLeave={(e) => {
                                             e.currentTarget.style.transform = 'translateY(0)';
                                             e.currentTarget.style.boxShadow = '';
                                         }}>

                                        {/* Discount Badge */}
                                        {discount > 0 && (
                                            <div className="ribbon-wrapper ribbon-xl">
                                                <div className="ribbon bg-success text-lg">
                                                    SAVE {discount}%
                                                </div>
                                            </div>
                                        )}

                                        {/* Popular Badge */}
                                        {isPopular && !discount && (
                                            <div className="ribbon-wrapper ribbon-lg">
                                                <div className="ribbon bg-primary">
                                                    POPULAR
                                                </div>
                                            </div>
                                        )}

                                        {/* Card Header */}
                                        <div className={`card-header text-center ${isPopular ? 'bg-primary' : 'bg-light'}`}>
                                            <h3 className={`card-title font-weight-bold text-lg mb-0 ${isPopular ? 'text-white' : ''}`}>
                                                {plan.name}
                                            </h3>
                                            {isPopular && <small className="text-white">⭐ Most Popular</small>}
                                        </div>

                                        {/* Card Body */}
                                        <div className="card-body text-center" style={{minHeight: '340px'}}>
                                            {/* Pricing */}
                                            <div className="mb-3">
                                                <h1 className="display-4 mb-0 font-weight-bold text-primary">
                                                    {monthlyLabel || formatPrice(plan.price)}
                                                </h1>
                                                {annualNote ? (
                                                    <p className="text-muted small mb-0">{annualNote}</p>
                                                ) : (
                                                    <p className="text-muted mb-0">per {formattedCycle}</p>
                                                )}
                                            </div>

                                            {/* Features */}
                                            <ul className="list-unstyled text-left mb-0">
                                                {(plan.features || [
                                                    'Full access to all features',
                                                    '24/7 Priority support',
                                                    'Free updates included',
                                                ]).map((feature, index) => (
                                                    <li key={index} className="mb-2">
                                                        <i className="fas fa-check-circle text-success mr-2"></i>
                                                        <span className="text-sm">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Card Footer - Info Only */}
                                        <div className="card-footer text-center bg-white">
                                            <div className="alert alert-info mb-0">
                                                <i className="fas fa-info-circle mr-2"></i>
                                                Contact us to subscribe
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <FeatureComparisonTable />

                    {/* What every plan includes */}
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="card card-primary card-outline">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <i className="fas fa-star mr-2"></i>
                                        All Plans Include
                                    </h3>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-3 col-sm-6 mb-3">
                                            <div className="info-box bg-gradient-info">
                                                <span className="info-box-icon">
                                                    <i className="fas fa-sync-alt"></i>
                                                </span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Auto Updates</span>
                                                    <span className="info-box-number">Always Latest</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-sm-6 mb-3">
                                            <div className="info-box bg-gradient-success">
                                                <span className="info-box-icon">
                                                    <i className="fas fa-database"></i>
                                                </span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Cloud Storage</span>
                                                    <span className="info-box-number">Unlimited Storage</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-sm-6 mb-3">
                                            <div className="info-box bg-gradient-warning">
                                                <span className="info-box-icon">
                                                    <i className="fas fa-headset"></i>
                                                </span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Support</span>
                                                    <span className="info-box-number">24/7 Available</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-sm-6 mb-3">
                                            <div className="info-box bg-gradient-danger">
                                                <span className="info-box-icon">
                                                    <i className="fas fa-shield-alt"></i>
                                                </span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Security</span>
                                                    <span className="info-box-number">SSL Encrypted</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="row mt-2">
                        <div className="col-12">
                            <div className="card card-secondary">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <i className="fas fa-shield-alt mr-2"></i>
                                        Why Choose Us?
                                    </h3>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-4 text-center mb-3">
                                            <div className="p-3">
                                                <i className="fas fa-clock fa-3x text-info mb-3"></i>
                                                <h5 className="font-weight-bold">24/7 Support</h5>
                                                <p className="text-muted">Round the clock customer support for all your needs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-center mb-3">
                                            <div className="p-3">
                                                <i className="fas fa-undo fa-3x text-success mb-3"></i>
                                                <h5 className="font-weight-bold">Money Back Guarantee</h5>
                                                <p className="text-muted">30-day money back guarantee if you're not satisfied</p>
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-center mb-3">
                                            <div className="p-3">
                                                <i className="fas fa-lock fa-3x text-warning mb-3"></i>
                                                <h5 className="font-weight-bold">Secure Payments</h5>
                                                <p className="text-muted">All payments are secured with industry standard encryption</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact CTA */}
                    <div className="row mt-2">
                        <div className="col-12">
                            <div className="card card-info">
                                <div className="card-body text-center">
                                    <h4 className="mb-3">
                                        <i className="fas fa-question-circle mr-2"></i>
                                        Have Questions About Our Plans?
                                    </h4>
                                    <p className="text-muted mb-3">
                                        Our team is here to help you choose the perfect plan for your business needs.
                                    </p>
                                    <a href="tel:+916382606218" className="btn btn-primary btn-lg">
                                        <i className="fas fa-phone mr-2"></i>
                                        Contact Sales: +91 63826 06218
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}